import axios, {
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
  type CancelTokenSource,
  type AxiosRequestConfig,
} from 'axios'

import { ERROR_TYPE } from '@/constants/consts'
import { API_URL, EXCEPT_CHECK_TOKEN_LIST } from '@/constants/api'

import router from '@/router'
import { useAccountStore, useUserStore } from '@/stores'

import { $dialog } from '@/plugins/dialog-alert'
import { $alert } from '@/plugins/simple-alert'

import type { AuthState, AccountState } from '@/types/user'
import type { ApiResponse, Params } from '@/types/api'
import { camelCase, snakeCase } from 'change-case/keys'

// Headers configuration
Object.assign(axios.defaults.headers.common, {
  'Cache-Control': 'no-cache',
  Pragma: 'no-cache',
  Expires: '0',
})

axios.interceptors.request.use(checkToken)
axios.interceptors.response.use(checkResponse, checkError)

let canceler: CancelTokenSource = axios.CancelToken.source()

export default {
  refreshCancelToken() {
    // 페이지 이동시 또는 request 취소할 필요가 있는경우 호출
    canceler.cancel()
    canceler = axios.CancelToken.source()
  },
  async get<T = unknown>(url: string, params?: unknown): Promise<ApiResponse<T>> {
    const parameter = snakeCase(params, -1)
    const config: AxiosRequestConfig = {
      params: parameter,
      cancelToken: canceler.token,
    }
    try {
      const res: AxiosResponse = await axios.get(url, config)
      return camelCase(res.data, -1) as ApiResponse<T>
    } catch (e) {
      return e as ApiResponse<T>
    }
  },
  async post<T = unknown>(url: string, params?: unknown): Promise<ApiResponse<T>> {
    const config: AxiosRequestConfig = { cancelToken: canceler.token }
    const parameter = snakeCase(params, -1)
    try {
      const res: AxiosResponse = await axios.post(url, parameter, config)
      // return res.data
      return camelCase(res.data, -1) as ApiResponse<T>
    } catch (e) {
      return e as ApiResponse<T>
    }
  },

  // 26-01-21 / kmkim / unused
  // async postFileUpload(url: string, params: Params<unknown>): Promise<ApiResponse<unknown>> {
  //   try {
  //     const res: AxiosResponse = await axios.post(url, params, {
  //       headers: {
  //         'Content-Type': 'multipart/form-data',
  //       },
  //     })
  //     return res.data
  //   } catch (e) {
  //     if (typeof e == 'string' && e.indexOf('ECONNRESET') > 0) {
  //       $alert.close()
  //     }
  //     return e as ApiResponse
  //   }
  // },

  // 26-01-21 / kmkim / unused
  // async postFileDown(url: string, params: Params<unknown>): Promise<AxiosResponse<Blob, unknown>> {
  //   try {
  //     const res: AxiosResponse = await axios.post(url, params, { responseType: 'blob' })
  //     const linkUrl = window.URL.createObjectURL(new Blob([res.data as BlobPart]))
  //     let fileName = res.headers['content-disposition']
  //     if (fileName == undefined) {
  //       return res.data
  //     }
  //     if (fileName) {
  //       fileName = fileName.split(';')[1]
  //       fileName = fileName.split('=')[1].replace(/"/g, "'")
  //       fileName = decodeURI(fileName)
  //     } else {
  //       fileName = 'filename'
  //     }
  //     const link = document.createElement('a')
  //     link.href = linkUrl
  //     link.setAttribute('download', fileName)
  //     document.body.appendChild(link)
  //     link.click()

  //     return Promise.resolve(res)
  //   } catch (e) {
  //     const error = e as AxiosError
  //     let resText = await new Promise<string>((resolve, reject) => {
  //       let reader = new FileReader()
  //       reader.addEventListener('abort', reject)
  //       reader.addEventListener('error', reject)
  //       reader.addEventListener('loadend', () => {
  //         resolve(reader.result as string)
  //       })
  //       const txt = error.response ? (error.response.data as Blob) : new Blob(['None'])
  //       reader.readAsText(txt)
  //     })
  //     const resData = JSON.parse(resText)
  //     $alert.error(resData.errorMsg || 'Unknown Error')
  //     return resData
  //   }
  // },

  async put<T = unknown>(url: string, params?: unknown): Promise<ApiResponse<T>> {
    const config: AxiosRequestConfig = { cancelToken: canceler.token }
    try {
      const parameter = snakeCase(params, -1)
      const res: AxiosResponse = await axios.put(url, parameter, config)
      return camelCase(res.data, -1) as ApiResponse<T>
    } catch (e) {
      return e as ApiResponse<T>
    }
  },

  async patch<T = unknown>(url: string, params?: unknown): Promise<ApiResponse<T>> {
    const config: AxiosRequestConfig = { cancelToken: canceler.token }
    try {
      const parameter = snakeCase(params, -1)
      const res: AxiosResponse = await axios.patch(url, parameter, config)
      return camelCase(res.data, -1) as ApiResponse<T>
    } catch (e) {
      return e as ApiResponse<T>
    }
  },

  async delete<T = unknown>(url: string, params?: unknown): Promise<ApiResponse<T>> {
    const parameter = snakeCase(params, -1)
    const config: AxiosRequestConfig = {
      cancelToken: canceler.token,
      data: parameter,
    }
    try {
      const res: AxiosResponse = await axios.delete(url, config)
      return camelCase(res.data, -1) as ApiResponse<T>
    } catch (e) {
      return e as ApiResponse<T>
    }
  },
}

/**
 *  토큰 체크 과정
 *  1. account, accessToken 이 필요 없으면 그대로 요청
 *  2. account, accessToken 이 없으면 login 화면 이동
 *  3. refresh 요청일 시 header에 refreshToken 담아 요청
 *  4. 그 외에는 header에 accessToken 담아 요청
 */
async function checkToken(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  const store = useAccountStore()
  const requestUrl = config.url || ''

  // 토큰이 존재할 경우(로그인 상태), 예외 목록 요청이더라도 Authorization 헤더를 첨부
  if (store.accessToken) {
    if (requestUrl == API_URL.AUTH.REFRESH) {
      config.headers.set('Authorization', 'Bearer ' + store.refreshToken)
    } else {
      config.headers.set('Authorization', 'Bearer ' + store.accessToken)
    }
    return config
  }

  // account 와 accessToken 이 필요없는 요청(login 등)
  if (EXCEPT_CHECK_TOKEN_LIST.includes(requestUrl)) return config

  // account 와 accessToken 없을 경우 login 화면 이동
  if (!store.userId || !store.accessToken) {
    const err = new Error('Session expired.') as Error & { isAuthRequired: true }
    err.isAuthRequired = true
    $alert.info('로그인이 필요합니다.')
    router.push('/login').catch(() => {})
    return Promise.reject(err)
  }

  return config
}

// check response
function checkResponse(resp: AxiosResponse): AxiosResponse {
  if (resp.data && resp.data.updatedToken) {
    // update 된 user info 가 있다면 sotre 갱신
    const store = useAccountStore()
    const payload: AuthState = {
      accessToken: resp.data.updatedToken,
      refreshToken: store.refreshToken,
      data: null,
    }
    store.setSession(payload)
  }
  return resp
}

// check error
async function checkError(e: AxiosError): Promise<AxiosResponse<unknown> | void> {
  // 로딩은 각 호출부의 finally에서 닫는다 (여기서 닫으면 다른 요청의 로딩까지 닫힘)
  // 수동으로 cancel 한 request 의 경우 리턴
  if (axios.isCancel(e) || e.code == 'ERR_CANCELED') {
    console.log('Request canceled by cancel token.')
    return
  }

  if ((e as unknown as { isAuthRequired?: boolean })?.isAuthRequired) {
    return Promise.reject(e)
  }

  console.error('API error occurred:', e)
  const response = e.response as AxiosResponse<ApiResponse> | undefined

  // 서버가 아예 응답하지 않은 경우(서버 다운, 네트워크 끊김 등) response 자체가 없다.
  // 이 경우를 별도로 처리하지 않으면 아래에서 response.data 접근 시 그대로 터진다.
  if (!response) {
    $alert.error('서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요.')
    return Promise.reject({
      errorCode: ERROR_TYPE.INTERNAL_SERVER_ERROR,
      errorMsg: '서버에 연결할 수 없습니다. 네트워크 상태를 확인해주세요.',
    })
  }

  console.log('response.data: ', response.data)

  // 우리 API는 항상 JSON({error_code, error_msg, data})으로 응답하므로, 응답 본문이
  // JSON이 아닌 문자열이라는 건 요청이 실제 백엔드까지 도달하지 못했다는 뜻이다
  // (예: dev 프록시가 백엔드에 연결하지 못해 자체적으로 만들어 보낸 500 에러 페이지).
  // 에러 문구에 특정 키워드(ECONNREFUSED 등)가 포함돼 있는지로 분기하던 방식은 프록시/
  // 서버 구현에 따라 문구가 달라지면 안 맞을 수 있어, 이 경우는 전부 "서버 연결 실패"로
  // 통일해서 안내한다.
  if (typeof response.data == 'string') {
    const msg = '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.'
    $alert.error(msg)
    console.error(e)
    return Promise.reject({
      errorCode: ERROR_TYPE.INTERNAL_SERVER_ERROR,
      errorMsg: msg,
    })
  }

  const resData = camelCase(response.data) as ApiResponse

  console.log(resData)
  //
  const store = useAccountStore()
  const msg = resData?.errorMsg
    ? `${resData.errorMsg}(code: ${resData.errorCode})`
    : '알 수 없는 오류. 관리자에게 문의하세요.'

  const errorCode = resData.errorCode
  const data = resData.data
  const url = response.config.url

  // errorCode 별 분기 후 execption 처리.
  if (errorCode == ERROR_TYPE.EXPIRED_ACCESSTOKEN_ERROR.errorCode) {
    // accessToken expired
    var rst = await store.refresh()
    if (rst && response?.config) {
      return axios.request(response.config)
    }
  } else if (errorCode == ERROR_TYPE.EXPIRED_REFRESH_TOKEN_ERROR.errorCode) {
    // refreshToken expired
    if (url !== '/api/login') {
      store.intervalClear()
      // error() 가 이전 콜백을 초기화하므로 콜백은 띄운 뒤에 등록한다.
      $dialog.error('세션 오류', '세션이 만료되었습니다.\n다시 로그인 해주세요.')
      $dialog.callback(() => {
        removeSession()
        router.push('/login').catch(() => {})
      })
    }
  } else if (errorCode == ERROR_TYPE.INVALID_TOKEN_ERROR.errorCode) {
    // Invalid token error
    if (url !== '/api/login') {
      $dialog.error('세션 오류', '세션이 오류가 발생했습니다.\n다시 로그인 해주세요.')
      $dialog.callback(() => {
        removeSession()
        router.push('/login').catch(() => {})
      })
    }
  } else if (errorCode == ERROR_TYPE.LOST_USER_LOGIN.errorCode) {
    // refreshToken expired
    if (url !== '/api/login') {
      $alert.error('로그인 유저정보가 존재하지 않습니다.\n다시 로그인 해주세요.')
      removeSession()
      router.push('/auth/login').catch(() => {})
    }
  } else if (errorCode == ERROR_TYPE.TOKEN_CONFIG_MODIFIED_ERROR.errorCode) {
    // 서버 설정에서 token expire 값을 변경. token 재 취득 필요.

    if (data && typeof data === 'object' && 'data' in data) {
      const payload = (data as { data: AuthState }).data
      await store.refreshAll(payload)
    }
    if (response?.config) return axios.request(response.config)
  } else if (errorCode == ERROR_TYPE.LOCKED_ACCOUNT_ERROR.errorCode) {
    // 계정 잠김 상태
    if (url !== '/api/login') {
      $alert.error('계정 오류\n계정이 잠겨있습니다.\n관리자에게 문의하세요.')
      removeSession()
      router.push('/auth/login').catch(() => {})
    }
  } else if (resData instanceof Blob) {
    return Promise.reject(e)
  } else if (
    errorCode == ERROR_TYPE.INVALID_ACCOUNT_ERROR.errorCode ||
    errorCode == ERROR_TYPE.LOCKED_ACCOUNT_ERROR.errorCode ||
    // 현재 비밀번호 불일치는 내 정보 수정 화면이 입력란 아래에 직접 안내한다.
    errorCode == ERROR_TYPE.INVALID_CURRENT_PASSWORD.errorCode
  ) {
    return Promise.reject(resData)
  } else if (errorCode == ERROR_TYPE.NO_PERMISSION.errorCode) {
    // 권한 없음 에러는 호출부(라우터 가드 등)에서 상황에 맞는 UI로 안내하므로
    // 여기서는 공용 토스트를 띄우지 않고 응답만 전달한다.
    return Promise.reject(resData)
  } else if (errorCode == ERROR_TYPE.LICENSE_ERROR.errorCode) {
    // 라이센스 에러. 로그인 화면으로 강제 이동.
    $dialog.error('라이센스 오류', '라이센스 인증이 필요합니다.')
    $dialog.callback(() => {
      if (resData?.data && typeof resData.data === 'string') {
        window.location.href = resData.data
      }
    })
  } else if (url === API_URL.AUTH.LOGIN) {
    return Promise.reject(resData)
  } else {
    // 미정의 에러
    $alert.error(msg)
  }

  return Promise.reject(resData)
}

function removeSession() {
  const userStore = useUserStore()
  userStore.logout()
}
